/**
 * Analytic hit-testing for annotation elements, in image-pixel units.
 *
 * Analytic rather than `SVGGeometryElement.isPointInStroke` on purpose: jsdom
 * implements neither `isPointInStroke` nor `getTotalLength`, so a DOM-based
 * version would be untestable in the `test:component:ci` suite and the hit
 * behaviour would only ever be exercised by a browser test.
 *
 * Tolerance is `max(strokePx / 2, MIN_GRAB_SCREEN_PX / displayScale)` — half the
 * drawn width, or a fixed screen-pixel grab radius converted into image pixels,
 * whichever is larger. The second term is what keeps a 1px hairline grabbable
 * when the canvas is zoomed out.
 */

import type { ImageAnnotationDict } from '../../../../../generated/nodeDataTypes'
import {
  effectiveFontPx,
  effectiveStrokePx,
  pickSupersample,
  toAnnotationKind,
  type Vec2,
} from './annotationGeometry'

/** Grab radius in screen pixels, converted to image pixels at the current scale. */
const MIN_GRAB_SCREEN_PX = 10

/**
 * Rough advance-width factor for the bundled bold faces, used ONLY to size the
 * text hit box. It never feeds the emitted geometry — the spec carries an anchor
 * point, and the bake measures the real font.
 */
const TEXT_WIDTH_FACTOR = 0.6

function toPixels(element: ImageAnnotationDict, width: number, height: number): Vec2[] {
  return element.points.map((point) => ({
    x: (point.x / 100) * width,
    y: (point.y / 100) * height,
  }))
}

function distanceToSegment(point: Vec2, a: Vec2, b: Vec2): number {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const lengthSquared = dx * dx + dy * dy
  if (lengthSquared === 0) return Math.hypot(point.x - a.x, point.y - a.y)
  let t = ((point.x - a.x) * dx + (point.y - a.y) * dy) / lengthSquared
  t = Math.min(1, Math.max(0, t))
  return Math.hypot(point.x - (a.x + t * dx), point.y - (a.y + t * dy))
}

function distanceToPolyline(point: Vec2, points: Vec2[]): number {
  if (points.length === 0) return Number.POSITIVE_INFINITY
  if (points.length === 1) return Math.hypot(point.x - points[0].x, point.y - points[0].y)
  let best = Number.POSITIVE_INFINITY
  for (let i = 0; i < points.length - 1; i++) {
    best = Math.min(best, distanceToSegment(point, points[i], points[i + 1]))
  }
  return best
}

function normalizedBox(points: Vec2[]): { x0: number; y0: number; x1: number; y1: number } {
  const xs = points.map((point) => point.x)
  const ys = points.map((point) => point.y)
  return { x0: Math.min(...xs), y0: Math.min(...ys), x1: Math.max(...xs), y1: Math.max(...ys) }
}

/** Distance to the rectangle *boundary* (a ring, not the filled interior). */
function distanceToRectRing(
  point: Vec2,
  box: { x0: number; y0: number; x1: number; y1: number },
): number {
  const corners: Vec2[] = [
    { x: box.x0, y: box.y0 },
    { x: box.x1, y: box.y0 },
    { x: box.x1, y: box.y1 },
    { x: box.x0, y: box.y1 },
    { x: box.x0, y: box.y0 },
  ]
  return distanceToPolyline(point, corners)
}

/** Approximate distance to an ellipse outline via the normalized radial metric. */
function distanceToEllipseRing(
  point: Vec2,
  box: { x0: number; y0: number; x1: number; y1: number },
): number {
  const cx = (box.x0 + box.x1) / 2
  const cy = (box.y0 + box.y1) / 2
  const rx = (box.x1 - box.x0) / 2
  const ry = (box.y1 - box.y0) / 2
  if (rx <= 0 || ry <= 0) return Math.hypot(point.x - cx, point.y - cy)
  const normalized = Math.hypot((point.x - cx) / rx, (point.y - cy) / ry)
  return Math.abs(normalized - 1) * Math.min(rx, ry)
}

function pointInTriangle(point: Vec2, a: Vec2, b: Vec2, c: Vec2): boolean {
  const sign = (p: Vec2, q: Vec2, r: Vec2): number =>
    (p.x - r.x) * (q.y - r.y) - (q.x - r.x) * (p.y - r.y)
  const d1 = sign(point, a, b)
  const d2 = sign(point, b, c)
  const d3 = sign(point, c, a)
  const hasNegative = d1 < 0 || d2 < 0 || d3 < 0
  const hasPositive = d1 > 0 || d2 > 0 || d3 > 0
  return !(hasNegative && hasPositive)
}

export interface HitTestArgs {
  /** Probe point in image pixels. */
  point: Vec2
  elements: ImageAnnotationDict[]
  width: number
  height: number
  /** Screen pixels per image pixel, from `displayScale()`. */
  scale: number
}

/**
 * Return the id of the topmost element under `point`, or null.
 *
 * Iterates last-to-first because array order is draw order: the last element is
 * painted on top, so it must also win the click.
 */
export function hitTestAnnotations(args: HitTestArgs): string | null {
  const { point, elements, width, height, scale } = args
  const supersample = pickSupersample(width, height)
  const grabTolerance = MIN_GRAB_SCREEN_PX / (scale > 0 ? scale : 1)

  for (let i = elements.length - 1; i >= 0; i--) {
    const element = elements[i]
    const kind = toAnnotationKind(element.kind)
    if (kind === null) continue
    const points = toPixels(element, width, height)

    if (kind === 'text') {
      const fontSizePct = element.font_size
      const text = element.text
      if (fontSizePct === undefined || text === undefined) continue
      const fontPx = effectiveFontPx(fontSizePct, height)
      // Anchor is left / ascender, so the box hangs down-right from the point.
      const boxWidth = TEXT_WIDTH_FACTOR * fontPx * text.length
      const anchor = points[0]
      if (
        point.x >= anchor.x - grabTolerance &&
        point.x <= anchor.x + boxWidth + grabTolerance &&
        point.y >= anchor.y - grabTolerance &&
        point.y <= anchor.y + fontPx + grabTolerance
      ) {
        return element.id
      }
      continue
    }

    const strokePx = effectiveStrokePx(element.stroke_width, width, height, supersample)
    const tolerance = Math.max(strokePx / 2, grabTolerance)

    switch (kind) {
      case 'stroke':
        if (distanceToPolyline(point, points) <= tolerance) return element.id
        break
      case 'rect':
        // Ring, not fill: an unfilled rectangle must not swallow clicks on
        // whatever sits behind it.
        if (distanceToRectRing(point, normalizedBox(points)) <= tolerance) return element.id
        break
      case 'ellipse':
        if (distanceToEllipseRing(point, normalizedBox(points)) <= tolerance) return element.id
        break
      case 'arrow': {
        const [tail, head] = points
        if (distanceToSegment(point, tail, head) <= tolerance) return element.id
        // The head triangle is wider than the shaft, so test it separately.
        const dx = head.x - tail.x
        const dy = head.y - tail.y
        const length = Math.hypot(dx, dy)
        if (length > 0) {
          const headLen = Math.min(4 * strokePx, length)
          const halfWidth = 2 * strokePx * (headLen / (4 * strokePx))
          const ux = dx / length
          const uy = dy / length
          const base: Vec2 = { x: head.x - headLen * ux, y: head.y - headLen * uy }
          const px = -uy
          const py = ux
          if (
            pointInTriangle(
              point,
              head,
              { x: base.x + halfWidth * px, y: base.y + halfWidth * py },
              { x: base.x - halfWidth * px, y: base.y - halfWidth * py },
            )
          ) {
            return element.id
          }
        }
        break
      }
    }
  }
  return null
}
